















































import { computed, defineComponent, onUnmounted, ref } from "@vue/composition-api";
import {
  FundraisingGoal,
  FundraisingGoalStatus,
  InputFundraisingGoalCreate,
  InputFundraisingGoalSearch,
} from "@scrile/api-provider/dist/api/FundraisingGoalsProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles from "@/hooks/useFiles";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BasePage from "@/components/BasePage";
import EmptyContent from "@/components/EmptyContent";
import FundraisingGoalCard from "@/components/FundraisingGoal/FundraisingGoalCard";
import { InputRewardCreate } from "@/components/FundraisingGoal/types";
import InfiniteScroll from "@/components/InfiniteScroll";
import PageTitle from "@/components/PageTitle/PageTitle";
import CreateGoalModal from "./components/CreateGoalModal.vue";
import FundraisingGoalsSetup from "./components/FundraisingGoalsSetup.vue";

export interface InputGoalData {
  goal: InputFundraisingGoalCreate;
  rewards: InputRewardCreate[];
}

export default defineComponent({
  name: "PageFundraisingGoals",
  components: {
    FundraisingGoalCard,
    CreateGoalModal,
    InfiniteScroll,
    BaseButton,
    BaseIcon,
    EmptyContent,
    BaseIconButton,
    PageTitle,
    BasePage,
    FundraisingGoalsSetup,
  },
  setup() {
    const { authUserStrict, authorities } = useAuthUser();
    const { uploadThenUpdate } = useFiles();

    const loading = ref(true);
    const createProcessing = ref(false);

    const showCreateModal = ref(false);

    const canUpdateGoals = computed(() => checkAuthorities(authorities.value, ["updateMyFundraisingGoal"]));

    const { enableScroll, items: goals, getFirstPage, getNextPage, addItem, updateItem } = usePager<
      FundraisingGoal,
      InputFundraisingGoalSearch
    >(page => {
      return providers.FundraisingGoalsProvider.search({
        data: {
          page,
          sort: "CREATE_TIME,DESC",
          userId: authUserStrict.value.id,
        },
      });
    });

    const hasActiveOrNewGoal = computed(() =>
      goals.value.some(goal => [FundraisingGoalStatus.NEW, FundraisingGoalStatus.ACTIVE].includes(goal.status))
    );

    const createRewards = async (data: InputRewardCreate[], goalId: string) => {
      if (!data.length) return;
      const newRewards = await providers.FundraisingGoalsProvider.rewardCreate({
        data: data.map(i => ({ ...i.data, goalId })),
      });
      const promises: Promise<any>[] = [];
      newRewards.forEach((i, index) => {
        const file = data[index].file;
        if (file) {
          promises.push(
            uploadThenUpdate({
              file,
              fileId: { subjectId: i.id, type: "fundraising_goal_reward" },
            })
          );
        }
      });
      await Promise.all(promises);
    };

    const createNewGoal = async (data: InputGoalData, cb?: () => void) => {
      if (!canUpdateGoals.value) return;
      try {
        createProcessing.value = true;
        const { goal, rewards } = data;
        const newGoal = await providers.FundraisingGoalsProvider.create({ data: goal });
        await createRewards(rewards, newGoal.id);
        addItem(newGoal);
        cb && cb();
        showCreateModal.value = false;
      } finally {
        createProcessing.value = false;
      }
    };

    const updateGoal = (item?: FundraisingGoal) => {
      if (!item) return;
      updateItem(item, i => i.id === item.id);
    };

    const getData = async () => {
      try {
        loading.value = true;
        await getFirstPage();
      } finally {
        loading.value = false;
      }
    };

    getData();

    emitter.on<FundraisingGoal>(EVENTS.FUNDRAISING_GOAL_UPDATED, updateGoal);

    onUnmounted(() => {
      emitter.off<FundraisingGoal>(EVENTS.FUNDRAISING_GOAL_UPDATED, updateGoal);
    });

    return {
      loading,
      createProcessing,
      enableScroll,
      authUserStrict,
      goals,
      hasActiveOrNewGoal,
      showCreateModal,
      canUpdateGoals,
      createNewGoal,
      updateGoal,
      getNextPage,
    };
  },
});
